/**
 * Leads Page — Enterprise redesign (Feb 2026)
 * Compact left-border KPI cards + Lead Source mini chart + dense data table with phone column.
 */
import { useState, useEffect, useRef, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import { Users, Lock, FileText, Code, Calendar, ShieldCheck, CheckCircle2, Phone as PhoneIcon, FlaskConical, RotateCw } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from "recharts";
import { startOfCurrentMonthMs } from "../utils/dateRanges";
import { SOURCE_ORDER, bucketLeadsBySource } from "../utils/leadSources";
import { isSuspectedTestLead } from "../utils/testLeads";
import TableScroll from "../components/TableScroll";
import TimeRangeSelector from "../components/dashboard/TimeRangeSelector";

// iter88 — Issue 2: lead-source bucketing centralised in
// ../utils/leadSources. The legacy SOURCE_ORDER + normalizeSource()
// constants were removed because they silently folded missing-source
// leads into "Direct" and included `lead.source` (lead TYPE).

// Canonical source buckets — see ../utils/leadSources.

const StatCardCompact = ({ accent, label, value, icon: Icon, testId }) => (
  <div
    className="bg-white rounded-lg border border-gray-200 px-4 py-3 flex items-center gap-3"
    style={{ borderLeft: `4px solid ${accent}`, minHeight: 86, maxHeight: 120 }}
    data-testid={testId}
  >
    <div className="w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: `${accent}15` }}>
      <Icon size={18} style={{ color: accent }} />
    </div>
    <div className="min-w-0">
      <p className="text-2xl font-bold text-gray-900 leading-tight tabular-nums">{value}</p>
      <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wider mt-0.5">{label}</p>
    </div>
  </div>
);

const SourcesTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0];
  return (
    <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-lg">
      <p className="font-semibold">{p.payload.name}</p>
      <p className="text-gray-300">{p.value.toLocaleString()} leads</p>
    </div>
  );
};

const LeadsPage = ({ onNavigateToLead, initialTab = "all", onTabChange }) => {
  const { apiFetch, user } = useAuth();
  const toast = useToast();
  const [leads, setLeads] = useState([]);
  const [unlockedLeads, setUnlockedLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUnlockedLoading, setIsUnlockedLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [filter, setFilter] = useState("all");
  // iter94 — Issue 14: Hide leads explicitly marked as Test/Demo. Off by default.
  const [hideTestLeads, setHideTestLeads] = useState(() => {
    try { return localStorage.getItem("vd_hide_test_leads") === "1"; } catch { return false; }
  });
  const toggleHideTestLeads = () => {
    setHideTestLeads(prev => {
      const next = !prev;
      try { localStorage.setItem("vd_hide_test_leads", next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };
  // iter95 — Issue 20: Real-time search across name/phone/email
  const [searchQuery, setSearchQuery] = useState("");
  // iter98 — Global date filter: drives lead list, KPIs, AND Lead Sources chart.
  // 0 = All Time. Persisted in localStorage so dealers don't have to reset on every visit.
  const [daysRange, setDaysRange] = useState(() => {
    try {
      const stored = localStorage.getItem("vd_leads_days_range");
      const n = stored !== null ? Number(stored) : 30;
      return [0, 7, 30, 90].includes(n) ? n : 30;
    } catch { return 30; }
  });
  const setDaysRangePersisted = (v) => {
    setDaysRange(v);
    try { localStorage.setItem("vd_leads_days_range", String(v)); } catch { /* ignore */ }
  };
  const dateRangeLabel = daysRange === 0 ? "All time" : `Last ${daysRange} days`;
  const dateRangeStartMs = useMemo(() => {
    if (daysRange === 0) return 0;
    return Date.now() - daysRange * 24 * 60 * 60 * 1000;
  }, [daysRange]);
  const [allLeadsError, setAllLeadsError] = useState(null);
  const [unlockedLeadsError, setUnlockedLeadsError] = useState(null);
  const errorShownRef = useRef(false);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (onTabChange) onTabChange(tab);
  };

  useEffect(() => {
    let isMounted = true;
    const fetchLeads = async () => {
      setIsLoading(true);
      setAllLeadsError(null);
      try {
        const params = filter === "verified" ? "?is_verified=true" : filter === "unverified" ? "?is_verified=false" : "";
        const res = await apiFetch(`/leads${params}`, { method: "GET" });
        if (isMounted) {
          setLeads(res.data || []);
          setIsLoading(false);
        }
      } catch (e) {
        if (isMounted) {
          console.error("All Leads fetch error:", e);
          setIsLoading(false);
          setAllLeadsError("Could not load leads");
        }
      }
    };
    fetchLeads();
    return () => { isMounted = false; };
  }, [apiFetch, filter]);

  useEffect(() => {
    let isMounted = true;
    const fetchUnlockedLeads = async () => {
      setIsUnlockedLoading(true);
      setUnlockedLeadsError(null);
      try {
        const res = await apiFetch("/unlock/leads", { method: "GET" });
        if (isMounted && res.data) {
          setUnlockedLeads(res.data.leads || []);
          setIsUnlockedLoading(false);
        }
      } catch (e) {
        if (isMounted) {
          console.error("Unlocked Leads fetch error:", e);
          setIsUnlockedLoading(false);
          setUnlockedLeadsError("Could not load unlocked leads");
        }
      }
    };
    fetchUnlockedLeads();
    return () => { isMounted = false; };
  }, [apiFetch]);

  // iter98 — Client-side CSV export of the *currently visible* leads so
  // the file always matches the filters/tab the dealer is looking at.
  const buildCSV = (rows) => {
    const headers = [
      "Name", "Phone", "Email", "Verified", "Lead Type", "Date Created",
      "Vehicle Interest", "Stock #", "VIN", "Price Shown", "Intent Score",
      "Lead Status", "Lead Source",
    ];
    const escape = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v);
      if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const fullName = (l) => {
      if (l.name) return l.name;
      return `${l.first_name || ""} ${l.last_name || ""}`.trim();
    };
    const verifiedFor = (l) => (l.is_verified || l.verified_mobile) ? "Yes" : "No";
    const vehicleFor = (l) => {
      const parts = [l.vehicle_year, l.vehicle_make, l.vehicle_model, l.vehicle_trim].filter(Boolean);
      return parts.join(" ");
    };
    const formatDate = (iso) => {
      if (!iso) return "";
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return "";
      return d.toISOString().replace("T", " ").slice(0, 19);
    };
    const lines = [headers.join(",")];
    for (const l of rows) {
      lines.push([
        escape(fullName(l)),
        escape(l.phone || ""),
        escape(l.email || ""),
        escape(verifiedFor(l)),
        escape(l.lead_type === "unlock" ? "Unlock" : "Standard"),
        escape(formatDate(l.created_at)),
        escape(vehicleFor(l)),
        escape(l.stock_number || ""),
        escape(l.vin || ""),
        escape(l.vehicle_price || ""),
        escape(l.intent_score_at_submit ?? ""),
        escape(l.status || ""),
        escape(l.source || l.utm_source || ""),
      ].join(","));
    }
    return lines.join("\n");
  };

  const exportLeads = () => {
    try {
      const rows = activeTab === "unlocked" ? visibleUnlockedLeads : visibleLeads;
      if (rows.length === 0) {
        toast.error("No leads to export with the current filters");
        return;
      }
      const csv = buildCSV(rows);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `leads-export-${today}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${rows.length} lead${rows.length === 1 ? "" : "s"}`);
    } catch (_e) {
      toast.error("Error exporting leads");
    }
  };

  const combined = [...leads, ...unlockedLeads];
  const uniqueById = useMemo(
    () => {
      // iter139 F — merging two independently-sorted arrays (`/leads`
      // and `/unlock/leads`) via Map dedup produces an interleaved
      // order that isn't globally newest-first. Re-sort here so every
      // table on this page reads chronologically top-to-bottom.
      const arr = Array.from(new Map(combined.map(l => [l.id, l])).values());
      arr.sort((a, b) => {
        const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
        const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
        return tb - ta;
      });
      return arr;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [leads, unlockedLeads]
  );

  // iter94 — Issue 14: Apply Hide-Test filter on top of every derived dataset.
  // KPIs, charts, and tables all use these post-filter arrays so the toggle is
  // consistent across the page.
  // iter95 — Issue 20: Search filter layers on top of Hide-Test.
  const matchesSearch = (lead, q) => {
    if (!q) return true;
    const needle = q.trim().toLowerCase();
    if (!needle) return true;
    const haystack = [
      lead.first_name, lead.last_name, lead.name,
      lead.phone, lead.email,
    ].filter(Boolean).join(" ").toLowerCase();
    return haystack.includes(needle);
  };
  const applyAllFilters = (arr) => {
    let out = hideTestLeads ? arr.filter(l => !l.is_test) : arr;
    if (searchQuery.trim()) out = out.filter(l => matchesSearch(l, searchQuery));
    // iter98 — date filter (applies everywhere on the page)
    if (dateRangeStartMs > 0) {
      out = out.filter(l => {
        const t = l.created_at ? new Date(l.created_at).getTime() : NaN;
        return Number.isFinite(t) && t >= dateRangeStartMs;
      });
    }
    return out;
  };
  const visibleUniqueById = useMemo(
    () => applyAllFilters(uniqueById),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [uniqueById, hideTestLeads, searchQuery, dateRangeStartMs]
  );
  const visibleLeads = useMemo(
    () => applyAllFilters(leads),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [leads, hideTestLeads, searchQuery, dateRangeStartMs]
  );
  const visibleUnlockedLeads = useMemo(
    () => applyAllFilters(unlockedLeads),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unlockedLeads, hideTestLeads, searchQuery, dateRangeStartMs]
  );
  const testLeadCount = uniqueById.filter(l => l.is_test).length;

  const totalLeads = visibleUniqueById.length;
  const verifiedCount = visibleUniqueById.filter(l => l.is_verified || l.verified_mobile).length;
  // iter87 — dealer-TZ-aware "Leads This Month" boundary. See
  // /app/frontend/src/utils/dateRanges.js for the helper. user.business_timezone
  // is hydrated by /auth/me from the dealer's address/state.
  const tz = user?.business_timezone || "America/New_York";
  const monthStartMs = startOfCurrentMonthMs(tz);
  const thisMonthCount = visibleUniqueById.filter(l => {
    const t = l.created_at ? new Date(l.created_at).getTime() : NaN;
    return Number.isFinite(t) && t >= monthStartMs;
  }).length;
  const unlockedCount = visibleUnlockedLeads.length;

  const sourceChartData = useMemo(
    () => bucketLeadsBySource(visibleUniqueById),
    [visibleUniqueById]
  );
  const hasSourceData = sourceChartData.some(d => d.value > 0);

  const showLoading = (activeTab === "all" && isLoading) || (activeTab === "unlocked" && isUnlockedLoading);
  if (showLoading && leads.length === 0 && unlockedLeads.length === 0) {
    return (
      <div className="page" data-testid="leads-page-loading">
        <div className="page-header">
          <div className="page-header-left">
            <h2 className="page-title">Customer Leads</h2>
            <p className="page-subtitle">Phone-verified contacts from your unlock CTAs</p>
          </div>
        </div>
        {/* KPI skeletons */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-gray-100 animate-pulse" />
              <div className="flex-1 space-y-2">
                <div className="h-3 w-20 bg-gray-100 rounded animate-pulse" />
                <div className="h-5 w-12 bg-gray-200 rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>
        {/* Table skeleton */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3" data-testid="leads-table-skeleton">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center gap-4 py-2 border-b border-gray-100 last:border-b-0">
              <div className="h-4 w-32 bg-gray-100 rounded animate-pulse" />
              <div className="h-4 w-28 bg-gray-100 rounded animate-pulse" />
              <div className="h-4 w-24 bg-gray-100 rounded animate-pulse" />
              <div className="h-4 w-20 bg-gray-100 rounded animate-pulse ml-auto" />
              <div className="h-6 w-16 bg-gray-100 rounded-full animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="page" data-testid="leads-page">
      <div className="page-header">
        <div className="page-header-left">
          <h2 className="page-title">Customer Leads</h2>
          <p className="page-subtitle">Phone-verified contacts from your unlock CTAs</p>
        </div>
        <div className="page-header-actions">
          {/* iter98 — Global date filter: drives the list AND the chart */}
          <TimeRangeSelector
            value={daysRange}
            onChange={setDaysRangePersisted}
            includeAllTime
            testId="leads-date-range"
          />
          {/* iter95 — Issue 20: Real-time search */}
          <div className="relative" data-testid="leads-search-wrapper">
            <svg
              aria-hidden
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
              width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search name, phone, email…"
              data-testid="leads-search-input"
              className="pl-8 pr-7 py-1.5 rounded-md border border-gray-200 text-[12.5px] focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400 w-[240px]"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                aria-label="Clear search"
                data-testid="leads-search-clear"
                className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-[14px] leading-none px-1"
              >×</button>
            )}
          </div>
          {/* iter94 — Issue 14: Hide Test Leads toggle */}
          <label
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-gray-200 bg-white text-[12.5px] text-gray-700 cursor-pointer hover:bg-gray-50 select-none"
            data-testid="hide-test-leads-toggle-label"
            title={testLeadCount > 0
              ? `${testLeadCount} lead${testLeadCount === 1 ? "" : "s"} marked as Test/Demo`
              : "No leads have been marked as Test yet"}
          >
            <FlaskConical size={13} className="text-amber-500" />
            <span>Hide Test Leads</span>
            {testLeadCount > 0 && (
              <span className="inline-flex items-center justify-center text-[10px] font-bold text-amber-700 bg-amber-100 border border-amber-200 rounded-full min-w-[18px] h-[18px] px-1">
                {testLeadCount}
              </span>
            )}
            <input
              type="checkbox"
              checked={hideTestLeads}
              onChange={toggleHideTestLeads}
              className="sr-only"
              data-testid="hide-test-leads-toggle"
            />
            <span
              className={`relative inline-block w-7 h-4 rounded-full transition-colors ${hideTestLeads ? "bg-amber-500" : "bg-gray-300"}`}
              aria-hidden
            >
              <span
                className="absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full shadow transition-transform"
                style={{ transform: hideTestLeads ? "translateX(12px)" : "translateX(0)" }}
              />
            </span>
          </label>
          {activeTab === "all" && (
            <select value={filter} onChange={(e) => setFilter(e.target.value)} className="filter-select" data-testid="leads-filter">
              <option value="all">All Leads</option>
              <option value="verified">Verified Only</option>
              <option value="unverified">Unverified Only</option>
            </select>
          )}
          <button className="btn-primary" onClick={exportLeads} data-testid="export-csv-btn">Export CSV</button>
        </div>
      </div>

      {/* Compact left-border KPI stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4" data-testid="leads-kpi-grid">
        <StatCardCompact accent="#005CFF" label="Total Leads" value={totalLeads.toLocaleString()} icon={Users} testId="leads-kpi-total" />
        <StatCardCompact accent="#2563EB" label="Leads This Month" value={thisMonthCount.toLocaleString()} icon={Calendar} testId="leads-kpi-month" />
        <StatCardCompact accent="#10B981" label="Verified Leads" value={verifiedCount.toLocaleString()} icon={ShieldCheck} testId="leads-kpi-verified" />
      </div>

      {/* Lead Source mini bar chart */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4" data-testid="lead-sources-mini-chart">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-[13px] font-semibold text-gray-900 m-0">Lead Sources</h3>
            <p className="text-[11px] text-gray-500 mt-0.5 m-0" data-testid="lead-sources-date-label">
              Where your leads came from &middot; {dateRangeLabel}
            </p>
          </div>
        </div>
        {!hasSourceData ? (
          <p className="text-[12px] text-gray-400 text-center py-6">No source data yet.</p>
        ) : (
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={sourceChartData} layout="vertical" margin={{ top: 2, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid horizontal={false} stroke="#F3F4F6" />
              <XAxis type="number" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} allowDecimals={false} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "#4B5563" }} axisLine={false} tickLine={false} width={80} />
              <Tooltip content={<SourcesTooltip />} cursor={{ fill: "#F9FAFB" }} />
              <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={14}>
                {sourceChartData.map((d, i) => (
                  <Cell key={i} fill={d.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Tabs */}
      <div className="leads-tabs">
        <button className={`leads-tab ${activeTab === "all" ? "active" : ""}`} onClick={() => handleTabChange("all")} data-testid="leads-tab-all">
          All Leads ({visibleLeads.length})
        </button>
        <button className={`leads-tab ${activeTab === "unlocked" ? "active" : ""}`} onClick={() => handleTabChange("unlocked")} data-testid="leads-tab-unlocked">
          <Lock size={12} style={{display:'inline', marginRight:'4px', verticalAlign:'middle'}} />
          Unlocked Leads ({unlockedCount})
        </button>
      </div>

      {/* All Leads Tab */}
      {activeTab === "all" && (
        <>
          {allLeadsError && (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded mb-4 text-sm">
              {allLeadsError} • <button onClick={() => window.location.reload()} className="underline">Retry</button>
            </div>
          )}
          {!allLeadsError && visibleLeads.length === 0 ? (
            <div className="empty-state-card" data-testid="leads-empty-state">
              <div className="empty-state-icon"><Users size={24} /></div>
              {searchQuery.trim() && leads.length > 0 ? (
                <>
                  <h3 data-testid="leads-empty-state-search">No results found for &quot;{searchQuery}&quot;</h3>
                  <p>Try searching for a different name, phone number, or email address.</p>
                  <button className="btn-secondary" onClick={() => setSearchQuery("")} data-testid="leads-clear-search-btn">
                    Clear Search
                  </button>
                </>
              ) : hideTestLeads && leads.length > 0 ? (
                <>
                  <h3>No non-test leads to show</h3>
                  <p>All current leads are flagged as Test/Demo. Toggle Hide Test Leads off to view them.</p>
                </>
              ) : (
                <>
                  <h3>No leads yet</h3>
                  <p>Contacts captured through your unlock CTA will appear here once visitors verify their phone.</p>
                  <button className="btn-secondary" onClick={() => window.open('/api/embed.js', '_blank')}>
                    <Code size={14} /> View Embed Script
                  </button>
                </>
              )}
            </div>
          ) : visibleLeads.length > 0 && (
            <TableScroll testId="leads-table-scroll">
            <table className="data-table" data-testid="leads-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Name</th>
                  <th>Phone</th>
                  {/* iter91 — Issue 5: Email + Source are deprioritised
                      at <lg viewports. They remain available on the
                      Lead Detail page. */}
                  <th className="hide-lg">Email</th>
                  <th className="hide-lg">Source</th>
                  <th>Verified</th>
                  <th>Date</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {visibleLeads.map(lead => {
                  const suspected = !lead.is_test && isSuspectedTestLead(lead);
                  return (
                  <tr
                    key={lead.id}
                    className="cursor-pointer"
                    onClick={() => onNavigateToLead && onNavigateToLead(lead.id)}
                    style={lead.is_test ? { background: "#FFFBEB" } : undefined}
                    data-testid={`lead-row-${lead.id}`}
                    data-is-test={lead.is_test ? "1" : "0"}
                  >
                    <td>
                      <div style={{ display: "inline-flex", gap: "4px", alignItems: "center", flexWrap: "wrap" }}>
                        {lead.lead_type === "unlock" ? (
                          <span className="badge unlocked" title="Unlocked Lead" style={{display:'inline-flex',alignItems:'center',gap:'3px'}}><Lock size={10}/> Unlock</span>
                        ) : (
                          <span className="badge standard" title="Standard Lead" style={{display:'inline-flex',alignItems:'center',gap:'3px'}}><FileText size={10}/> Standard</span>
                        )}
                        {lead.is_test && (
                          <span
                            title="This lead is marked as Test/Demo"
                            data-testid={`lead-test-badge-${lead.id}`}
                            style={{
                              display: "inline-flex", alignItems: "center", gap: "3px",
                              fontSize: "10px", fontWeight: 700, color: "#92400e",
                              background: "#FEF3C7", border: "1px solid #FDE68A",
                              padding: "1px 6px", borderRadius: "999px", textTransform: "uppercase",
                            }}
                          >
                            <FlaskConical size={9} /> Test
                          </span>
                        )}
                        {suspected && (
                          <span
                            title="Name contains a common test pattern (test/iter/demo). Click into the lead to confirm or mark it as Test."
                            data-testid={`lead-suspected-test-badge-${lead.id}`}
                            aria-label="Possibly a test lead"
                            style={{
                              display: "inline-flex", alignItems: "center", justifyContent: "center",
                              width: "16px", height: "16px", borderRadius: "999px",
                              fontSize: "10px", fontWeight: 700, color: "#92400e",
                              background: "#FFFBEB", border: "1px dashed #FCD34D",
                              lineHeight: 1,
                            }}
                          >
                            ?
                          </span>
                        )}
                        {/* iter96 — Issue 15: multiple price attempts badge */}
                        {(lead.price_attempt_count || 0) > 1 && (
                          <span
                            data-testid={`lead-multi-attempt-badge-${lead.id}`}
                            title={`This shopper has inquired about pricing ${lead.price_attempt_count} times on the same or different vehicles`}
                            style={{
                              display: "inline-flex", alignItems: "center", gap: "3px",
                              fontSize: "10px", fontWeight: 700, color: "#5B21B6",
                              background: "#F5F3FF", border: "1px solid #DDD6FE",
                              padding: "1px 6px", borderRadius: "999px",
                            }}
                          >
                            <RotateCw size={9} /> {lead.price_attempt_count}× price attempts
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="font-medium text-gray-900">{lead.name || `${lead.first_name || ""} ${lead.last_name || ""}`.trim() || "-"}</td>
                    <td>
                      {lead.phone ? (
                        <a
                          href={`tel:${lead.phone}`}
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 text-purple-700 hover:text-purple-900 hover:underline font-mono text-[12.5px]"
                          data-testid={`lead-phone-${lead.id}`}
                        >
                          <PhoneIcon size={11} /> {lead.phone}
                        </a>
                      ) : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="text-gray-600 hide-lg">{lead.email || "-"}</td>
                    <td className="hide-lg">{lead.utm_source || lead.source || <span className="text-gray-400">Direct</span>}</td>
                    <td>
                      {lead.is_verified || lead.verified_mobile ? (
                        <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full text-[11px] font-semibold" data-testid={`lead-verified-${lead.id}`}>
                          <CheckCircle2 size={11} /> Verified
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full text-[11px] font-medium" data-testid={`lead-unverified-${lead.id}`}>
                          Unverified
                        </span>
                      )}
                    </td>
                    <td className="text-gray-600">{new Date(lead.created_at).toLocaleDateString()}</td>
                    <td>
                      <button
                        className="text-purple-600 hover:text-purple-800 text-[12.5px] font-medium"
                        onClick={(e) => { e.stopPropagation(); onNavigateToLead && onNavigateToLead(lead.id); }}
                        data-testid={`view-lead-${lead.id}`}
                      >
                        View →
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            </TableScroll>
          )}
        </>
      )}

      {/* Unlocked Leads Tab */}
      {activeTab === "unlocked" && (
        <>
          {unlockedLeadsError && (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded mb-4 text-sm">
              {unlockedLeadsError} • <button onClick={() => window.location.reload()} className="underline">Retry</button>
            </div>
          )}
          {!unlockedLeadsError && visibleUnlockedLeads.length === 0 ? (
            <div className="empty-state-card" data-testid="unlocked-leads-empty">
              <div className="empty-state-icon"><Lock size={24} /></div>
              {searchQuery.trim() && unlockedLeads.length > 0 ? (
                <>
                  <h3 data-testid="unlocked-leads-empty-state-search">No results found for &quot;{searchQuery}&quot;</h3>
                  <p>Try searching for a different name, phone number, or email address.</p>
                  <button className="btn-secondary" onClick={() => setSearchQuery("")} data-testid="unlocked-leads-clear-search-btn">
                    Clear Search
                  </button>
                </>
              ) : hideTestLeads && unlockedLeads.length > 0 ? (
                <>
                  <h3>No non-test unlocked leads</h3>
                  <p>All current unlocked leads are flagged as Test/Demo. Toggle Hide Test Leads off to view them.</p>
                </>
              ) : (
                <>
                  <h3>No unlocked leads yet</h3>
                  <p>When visitors verify their phone to unlock pricing on your inventory, they&apos;ll appear here with full vehicle and intent data.</p>
                </>
              )}
            </div>
          ) : visibleUnlockedLeads.length > 0 && (
            <TableScroll testId="unlocked-leads-table-scroll">
            <table className="data-table" data-testid="unlocked-leads-table">
              <thead>
                <tr>
                  <th>Contact</th>
                  <th>Phone</th>
                  <th>Vehicle</th>
                  {/* iter91 — Issue 5: VIN and Intent Score deprioritised
                      at <lg so the priority columns (Contact/Phone/
                      Vehicle/Verified/Unlocked) fit without scrolling. */}
                  <th className="hide-lg">VIN</th>
                  <th className="hide-md">Intent Score</th>
                  <th>Verified</th>
                  <th>Unlocked</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {visibleUnlockedLeads.map(lead => (
                  <tr
                    key={lead.id}
                    className="cursor-pointer"
                    onClick={() => onNavigateToLead && onNavigateToLead(lead.id)}
                    style={lead.is_test ? { background: "#FFFBEB" } : undefined}
                    data-testid={`unlocked-lead-row-${lead.id}`}
                  >
                    <td>
                      <div className="lead-contact">
                        <span className="lead-name">
                          {lead.first_name} {lead.last_name || ""}
                          {lead.is_test && (
                            <span
                              data-testid={`unlocked-lead-test-badge-${lead.id}`}
                              style={{
                                marginLeft: "6px",
                                fontSize: "9px", fontWeight: 700, color: "#92400e",
                                background: "#FEF3C7", border: "1px solid #FDE68A",
                                padding: "1px 5px", borderRadius: "999px", textTransform: "uppercase",
                              }}
                            >TEST</span>
                          )}
                          {(lead.price_attempt_count || 0) > 1 && (
                            <span
                              data-testid={`unlocked-lead-multi-attempt-badge-${lead.id}`}
                              title={`${lead.price_attempt_count} price attempts on same or different vehicles`}
                              style={{
                                marginLeft: "6px",
                                display: "inline-flex", alignItems: "center", gap: "3px",
                                fontSize: "9px", fontWeight: 700, color: "#5B21B6",
                                background: "#F5F3FF", border: "1px solid #DDD6FE",
                                padding: "1px 6px", borderRadius: "999px",
                              }}
                            >
                              <RotateCw size={8} /> {lead.price_attempt_count}×
                            </span>
                          )}
                        </span>
                        {lead.email && <span className="lead-email">{lead.email}</span>}
                      </div>
                    </td>
                    <td>
                      {lead.phone ? (
                        <a
                          href={`tel:${lead.phone}`}
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 text-purple-700 hover:text-purple-900 hover:underline font-mono text-[12.5px]"
                        >
                          <PhoneIcon size={11} /> {lead.phone}
                        </a>
                      ) : <span className="text-gray-400">—</span>}
                    </td>
                    <td>
                      <span className="vehicle-info">
                        {[lead.vehicle_year, lead.vehicle_make, lead.vehicle_model].filter(Boolean).join(" ") || "-"}
                      </span>
                      {lead.vehicle_price && (
                        <span className="vehicle-price">${lead.vehicle_price.toLocaleString()}</span>
                      )}
                    </td>
                    <td className="hide-lg"><code className="vin-code">{lead.vin || "-"}</code></td>
                    <td className="hide-md">
                      {lead.intent_score_at_submit !== null ? (
                        <span className={`intent-score ${lead.intent_score_at_submit >= 70 ? "high" : lead.intent_score_at_submit >= 40 ? "medium" : "low"}`}>
                          {lead.intent_score_at_submit}
                        </span>
                      ) : "-"}
                    </td>
                    <td>
                      {lead.verified_mobile ? (
                        <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full text-[11px] font-semibold">
                          <CheckCircle2 size={11} /> Verified
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full text-[11px] font-medium">Pending</span>
                      )}
                    </td>
                    <td className="text-gray-600">{lead.unlocked_at ? new Date(lead.unlocked_at).toLocaleDateString() : "-"}</td>
                    <td>
                      <button
                        className="text-purple-600 hover:text-purple-800 text-[12.5px] font-medium"
                        onClick={(e) => { e.stopPropagation(); onNavigateToLead && onNavigateToLead(lead.id); }}
                        data-testid={`view-unlocked-lead-${lead.id}`}
                      >
                        View →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </TableScroll>
          )}
        </>
      )}

      <style>{`
        .leads-tabs {
          display: flex;
          gap: 4px;
          margin-bottom: 14px;
          border-bottom: 1px solid #E5E7EB;
        }
        .leads-tab {
          padding: 8px 14px;
          border: none;
          background: transparent;
          font-size: 13px;
          font-weight: 500;
          color: #6B7280;
          cursor: pointer;
          border-bottom: 2px solid transparent;
          margin-bottom: -1px;
          transition: all 0.15s;
        }
        .leads-tab:hover { color: #374151; }
        .leads-tab.active { color: #005CFF; border-bottom-color: #005CFF; }
        .badge.unlocked {
          background: #F3E8FF; color: #6B21A8;
          padding: 3px 7px; border-radius: 4px; font-size: 11px; font-weight: 600;
        }
        .badge.standard {
          background: #F3F4F6; color: #4B5563;
          padding: 3px 7px; border-radius: 4px; font-size: 11px; font-weight: 500;
        }
        .lead-contact { display: flex; flex-direction: column; gap: 1px; }
        .lead-name { font-weight: 600; color: #111827; font-size: 13px; }
        .lead-email { font-size: 11px; color: #6B7280; }
        .vehicle-info { display: block; font-weight: 500; font-size: 13px; }
        .vehicle-price { display: block; font-size: 11px; color: #059669; font-weight: 600; }
        .vin-code { font-family: monospace; font-size: 11px; background: #F3F4F6; padding: 2px 6px; border-radius: 3px; }
        .intent-score { display: inline-block; padding: 2px 8px; border-radius: 10px; font-weight: 600; font-size: 11px; }
        .intent-score.high { background: #D1FAE5; color: #065F46; }
        .intent-score.medium { background: #FEF3C7; color: #92400E; }
        .intent-score.low { background: #F3F4F6; color: #6B7280; }
      `}</style>
    </div>
  );
};

export default LeadsPage;
